import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import dompurify from 'isomorphic-dompurify';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { db } from '../../firebase/firebase';
import useInput from '../../hooks/inputHook';
import { useAuth } from '../../providers/AuthProvider';

const Description = (props) => {
  const { query, description } = props;
  const { isAdmin, uid, user } = useAuth();
  const [isEditing, setEditing] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [desc, setDesc] = useState(false);
  const [error, setError] = useState();
  const { enqueueSnackbar } = useSnackbar();

  const saveDescription = async (payload) => {
    try {
      if (query) {
        await setDoc(doc(db, 'descriptions', query), payload);
      }
    } catch (err) {
      setError(err);
    }
  };

  const {
    value: content,
    setValue: setContent,
    bind: bindContent,
    reset: resetContent,
  } = useInput('');

  useEffect(() => {
    if (description && description.content) {
      setDesc(description.content);
    } else {
      setDesc(false);
    }
  }, [description]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    const modified = serverTimestamp();
    saveDescription({
      content,
      modified,
      author: {
        username: user.username,
        uid,
      },
    });
    if (error) {
      enqueueSnackbar('Error saving description.', { variant: 'error' });
    } else {
      enqueueSnackbar('Description saved.', { variant: 'success' });
      setDesc(content);
    }
    setSaving(false);
    setEditing(false);
  };

  const handleEditButton = () => {
    setEditing(true);
    if (description && description.content) {
      setContent(description.content);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    resetContent();
  };

  if (!description && !isAdmin) {
    return null;
  }

  if (isEditing) {
    return (
      <div className="search-description-editing mb-4">
        <form onSubmit={handleSubmit}>
          <div className="field">
            <div className="control">
              <textarea
                className="textarea"
                placeholder="Type a description for this query"
                rows="5"
                {...bindContent}
              />
            </div>
          </div>
          <div className="field is-grouped">
            <div className="control is-expanded">
              <button
                type="submit"
                className={
                  isSaving
                    ? 'button is-fullwidth is-loading'
                    : 'button is-fullwidth'
                }
              >
                Save
              </button>
            </div>
            <div className="control">
              <button
                type="button"
                className="button is-light"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  const purifiedContent = dompurify.sanitize(desc);

  return (
    <div className="search-description mb-4">
      {desc ? (
        <div
          className="search-description-content is-font-brand content is-medium"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: purifiedContent }}
        />
      ) : (
        <div className="search-description-content is-font-brand content is-medium">
          <div className="notification is-warning mb-2">No description set</div>
        </div>
      )}
      {isAdmin ? (
        <div className="search-description-buttons">
          <button
            type="button"
            className="button is-fullwidth"
            onClick={handleEditButton}
          >
            Edit
          </button>
        </div>
      ) : (
        ''
      )}
      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx>
        {`
        .search-description-content {
          white-space: pre-line;
        }
      `}
      </style>
    </div>
  );
};

Description.propTypes = {
  query: PropTypes.string.isRequired,
  description: PropTypes.shape({
    content: PropTypes.string.isRequired,
  }),
};

Description.defaultProps = {
  description: null,
};

export default Description;
